<template>
<div class="checkout-content">
  <div class="left-panel">
    <button class="back-button" @click="$emit('close')">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.5 12.5L5.5 8L10.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>{{ $t('cpts.checkout.back') }}</span>
    </button>

    <h2>{{ $t('cpts.checkout.title') }}</h2>
    <!-- 初始化或通用错误提示 -->
    <p v-if="generalError" class="error-message">{{ generalError }}</p>
    <p v-if="initError" class="error-message">{{ initError }}</p>

    <!-- 订单名称 -->
    <div class="order-title">{{ orderTitle }}</div>
    <!-- 应付合计 -->
    <div class="amount large">${{ formatPaymentAmount(displayTotal) }}</div>
    <ul class="summary">
      <!-- 订单名称和原始小计 -->
      <li>
        <span>{{ orderTitle }}</span>
        <span>${{ formatPaymentAmount(subtotal) }}</span>
      </li>
      <!-- 应付小计 -->
      <li class="border-top">
        <span>{{ $t('cpts.checkout.subtotal') }}</span>
        <span>${{ formatPaymentAmount(subtotal) }}</span>
      </li>
      <!-- 优惠券 -->
      <li class="coupon">
        <el-input
          v-model="couponCode"
          :placeholder="$t('cpts.checkout.couponPlaceholder')"
          @input="clearCouponMessages"
          clearable
          class="coupon-input"
        >
          <template #append>
            <el-button @click="applyCoupon">{{ $t('cpts.checkout.applyCoupon') }}</el-button>
          </template>
        </el-input>
      </li>
      <!-- 优惠券错误/成功提示 -->
      <li v-if="couponError" class="error-message">{{ couponError }}</li>
      <li v-if="couponSuccess" class="success-message">{{ couponSuccess }}</li>

      <li v-if="appliedPromotionDiscount > 0" class="promotion-row">
        <div>
          <strong>{{ $t('activity.summer.payment_title') }}</strong>
          <small>{{ promotionAppliedText }}</small>
        </div>
        <em>{{ $t('activity.summer.save') }} ${{ formatPaymentAmount(appliedPromotionDiscount) }}</em>
      </li>

      <!-- 折扣 -->
      <li v-if="discount > 0">
        <span>{{ $t('cpts.checkout.couponDiscount') }}</span>
        <span>-${{ formatPaymentAmount(discount) }}</span>
      </li>
      <!-- 应付合计 -->
      <li class="border-top total-row">
        <span>{{ $t('cpts.checkout.totalPayable') }}</span>
        <span>${{ formatPaymentAmount(displayTotal) }}</span>
      </li>
    </ul>
  </div>

  <div class="right-panel">
    <h3>{{ $t('cpts.checkout.balancePaymentTitle') }}</h3>
    <p>{{ $t('cpts.checkout.balance') }}：<strong>${{ formatPaymentAmount(balance) }}</strong></p>
    <p v-if="showCredit">{{ $t('cpts.checkout.credit') }}：<strong>${{ formatPaymentAmount(credit) }}</strong></p>
    <button
      :disabled="disablePay"
      @click="pay"
      class="btn-pay"
    >
      {{ parseFloat(displayTotal) > (balance + credit) ? $t('cpts.checkout.insufficientBalance') : $t('cpts.checkout.payWithBalance') }}
    </button>
    <!-- 支付错误提示 -->
    <p v-if="payError" class="error-message">{{ payError }}</p>
  </div>
</div>
</template>

<script>
import { formatPaymentAmount } from '@/util/payment-amount';
import { initCheckout, applyCoupon, checkoutPay } from '@/http/api/order'
import { promotionConfig, promotionText } from '@/config/site-theme'
import { createRequestKey } from '@/util/request-key'

export default {
  name: 'Checkout',
  props: {
    order_no: {
      type: String,
      required: true
    },
    couponLocked: {
      type: Boolean,
      default: false
    },
    promotionDiscount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      orderTitle: '', // 从后端获取订单名称
      subtotal: 0,    // 从后端获取小计
      appliedCouponCode: '',
      discount: 0,    // 优惠券折扣
      balance: 0,     // 从后端获取用户余额
      showCredit: false, // 显示信用额度
      credit: 0,      // 信用额度
      couponCode: '', // 用户输入的优惠券码
      isPaying: false,// 支付状态
      initError: '',  // 初始化错误信息
      couponError: '',// 优惠券错误信息
      couponSuccess: '', // 优惠券成功信息
      payError: '',   // 支付错误信息
      generalError: '', // 通用错误信息 (如缺少订单号)
      appliedPromotionDiscount: 0,
      paymentRequestKey: '',
    }
  },
  computed: {
    // 计算最终应付金额
    displayTotal() {
      const total = this.subtotal - this.discount;
      return Math.max(0, total); // 确保总金额不小于0
    },
    disablePay() {
      const calculatedTotal = Math.max(0, this.subtotal - this.discount);
      const insufficientBalance = calculatedTotal > (this.balance + this.credit);
      const shouldDisable = this.isPaying || insufficientBalance || this.generalError || this.initError;
      return !!shouldDisable; // 返回 true 表示应该禁用按钮
    },
    autoCouponCode() {
      return promotionConfig.code;
    },
    promotionAppliedText() {
      return promotionText(this, 'payment_applied');
    }
  },
  methods: {
    formatPaymentAmount,
    // 初始化：获取订单信息和用户余额
    async fetchInit() {
      this.initError = ''; // 清空之前的错误
      try {
        const res = await initCheckout({
            order_no: this.order_no
        });
        const orderName = res.data.order_name;
        this.orderTitle = orderName === 'Proxy renewal'
          ? this.$t('cpts.checkout.proxyRenewal')
          : orderName; // 更新订单名称
        this.subtotal = parseFloat(res.data.subtotal); // 更新小计
        this.showCredit = res.data.show_credit;
        this.credit = parseFloat(res.data.credit);  // 更新信用额度
        this.balance  = parseFloat(res.data.balance);  // 更新余额
        this.couponCode = res.data.coupon || '';
        this.discount = Number(res.data.discount_price || 0);
        this.appliedCouponCode = this.couponCode;
        this.appliedPromotionDiscount = this.isActivityCoupon(this.couponCode) ? this.discount : 0;
        if (this.couponCode && this.discount > 0 && !this.appliedPromotionDiscount) {
          this.couponSuccess = this.$t('cpts.checkout.success.couponApplied');
        }
      } catch (err) {
        console.error('初始化失败:', err);
        this.initError = err.response?.data?.message || this.$t('cpts.checkout.error.initFailed');
      }
    },
    // 应用优惠券
    async applyCoupon() {
      this.couponError = ''; // 清空之前的错误
      this.couponSuccess = ''; // 清空之前的成功信息

      try {
        // 使用 GET 请求，传递优惠券码和订单号
        const res = await applyCoupon({
          coupon: this.couponCode,
          order_no: this.order_no // 传递订单号
        });
        this.appliedCouponCode = this.couponCode;
        this.subtotal = Number(res.data.subtotal);
        this.discount = parseFloat(res.data.discount); // 更新折扣金额
        this.appliedPromotionDiscount = this.isActivityCoupon(this.couponCode) ? this.discount : 0;
        this.couponSuccess = this.$t('cpts.checkout.success.couponApplied');
      } catch (err) {
        console.error('应用优惠券失败:', err);
        // 根据后端返回的错误信息提示用户
        this.couponError = err.response?.data?.message || this.$t('cpts.checkout.error.invalidCoupon');
        this.discount = 0; // 应用失败则清空折扣
        this.appliedPromotionDiscount = 0;
      }
    },
    // 执行支付
    async pay() {
      this.isPaying = true;
      this.payError = ''; // 清空之前的错误
      try {
          if (this.couponCode || this.appliedCouponCode) {
              await this.applyCoupon();
              if (this.couponError) throw new Error(this.couponError);
          }
          this.paymentRequestKey = this.paymentRequestKey || createRequestKey('wallet');
          const res = await checkoutPay({
              coupon: this.couponCode,
              order_no: this.order_no,
              request_key: this.paymentRequestKey
          });
          if (res.data.payment_channel !== 'wallet') {
            throw new Error('Unexpected checkout payment response');
          }
          this.$emit('success');
      } catch (err) {
          console.error('支付失败:', err);
          // 根据后端返回的错误信息提示用户
          this.payError = err.response?.data?.message || this.$t('cpts.checkout.error.payFailed');
      } finally {
          this.isPaying = false;
      }
    },
    // 清空优惠券相关的提示信息
    clearCouponMessages() {
      this.couponError = '';
      this.couponSuccess = '';
      this.discount = 0;
      this.appliedPromotionDiscount = 0;
    },
    isActivityCoupon(code) {
      return String(code || '').toLowerCase() === String(promotionConfig.code || '').toLowerCase();
    }
  },
  mounted() {
    // 组件挂载后立即获取初始化数据
    if (this.order_no) {
       this.fetchInit();
    } else {
       console.error('missingOrderInfo (order_no)');
       this.generalError = this.$t('cpts.checkout.error.missingOrderInfo');
    }
  }
}
</script>

<style scoped>

.checkout-content {
  max-width: 1000px;
  margin: auto;
  border-radius: 22px;
  border: 1px solid rgba(255, 138, 61, 0.18);
  box-shadow: 0 28px 70px rgba(18, 110, 106, 0.18);
  display: flex;
  overflow: hidden;
  margin-top: 80px;
  min-height: 680px;
  background: #fff;
}

.left-panel {
  position: relative;
  flex: 1;
  padding: 4rem 2rem;
  background:
    radial-gradient(circle at 18% 12%, rgba(247, 201, 72, 0.26), transparent 24%),
    radial-gradient(circle at 92% 18%, rgba(255, 138, 61, 0.14), transparent 24%),
    linear-gradient(145deg, #f7fcf8 0%, #fff4df 52%, #eaf7f3 100%);
  color: #173b3a;
  overflow-y: auto;
}

.left-panel h2 { margin-bottom: 1rem; font-size: 1.2rem; }
.left-panel .order-title { font-size: 1.1rem; margin-bottom: 0.5rem; }
.left-panel .amount.large {
  width: max-content;
  min-width: 220px;
  margin-bottom: 1.5rem;
  padding: 14px 18px;
  border-radius: 18px;
  font-size: 2.25rem;
  font-weight: 700;
  color: #173b3a;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 138, 61, 0.26);
  box-shadow: 0 14px 34px rgba(255, 138, 61, 0.12);
}
.summary { list-style: none; padding: 0; }
.summary li { 
  display: flex; 
  justify-content: space-between; 
  align-items: center;
  gap: 16px;
  margin: .45rem 0;
  padding: .65rem 0 0;
  color: #173b3a;
}
.summary li.border-top {
  border-top: 1px solid rgba(18, 110, 106, 0.14);
}
.summary .coupon {
  display: flex;
  width: 100%;
  opacity: 0.88;
  padding: 10px;
  border: 1px solid rgba(255, 138, 61, 0.22);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.86), rgba(255, 247, 231, 0.66));
  box-shadow: 0 12px 28px rgba(255, 138, 61, 0.08);
  transition: opacity 0.3s ease;
}

.summary .coupon:hover {
  opacity: 1;  /* hover 时恢复正常显示 */
}

.summary .coupon .coupon-input {
  width: 100%;
}

.summary .coupon ::v-deep .el-input-group {
  height: 52px;
  overflow: hidden;
  border: 1px solid rgba(18, 110, 106, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 8px 18px rgba(18, 110, 106, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.summary .coupon ::v-deep .el-input-group:focus-within {
  border-color: rgba(255, 138, 61, 0.72);
  box-shadow: 0 0 0 4px rgba(255, 138, 61, 0.12), 0 10px 24px rgba(255, 138, 61, 0.1);
}

.summary .coupon ::v-deep .el-input__inner {
  height: 52px;
  line-height: 52px;
  background-color: #fff;
  color: #173b3a;
  border: 0;
  font-weight: 500;
  box-shadow: none;
  transition: all 0.3s ease;
}

.summary .coupon:hover ::v-deep .el-input-group {
  border-color: rgba(255, 138, 61, 0.5);
}

.summary .coupon ::v-deep .el-input-group__append {
  width: 148px;
  height: 52px;
  background-color: rgba(255, 248, 233, 0.82);
  border: 0;
  border-left: 1px solid rgba(18, 110, 106, 0.14);
  color: #126e6a;
  padding: 0;
  text-align: center;
  vertical-align: middle;
  transition: all 0.3s ease;
}

.summary .coupon ::v-deep .el-input-group__append .el-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 12px);
  height: 40px;
  line-height: 40px;
  margin: 6px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #ff8a3d, #f7b955);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 8px 18px rgba(255, 138, 61, 0.22);
}

.summary .coupon ::v-deep .el-input-group__append .el-button:hover {
  background: linear-gradient(135deg, #ff7a22, #f4aa37);
  color: #fff;
  box-shadow: 0 10px 22px rgba(255, 122, 34, 0.28);
}
.right-panel {
  width: 300px;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.96);
  overflow-y: auto;
}

/* 添加卡片样式 */
.right-panel p {
  background: linear-gradient(135deg, rgba(255, 244, 223, 0.58), rgba(234, 247, 243, 0.68));
  border: 1px solid rgba(255, 138, 61, 0.22);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #5f7472;
  transition: all 0.2s ease;
}

.right-panel p:hover {
  border-color: rgba(255, 138, 61, 0.36);
  box-shadow: 0 10px 22px rgba(255, 138, 61, 0.08);
}

.right-panel p strong {
  display: block;
  font-size: 24px;
  color: #173b3a;
  margin-top: 8px;
  font-weight: 600;
}

/* 调整标题样式 */
.right-panel h3 {
  margin-bottom: 1.5rem;
  font-size: 16px;
  color: #173b3a;
  font-weight: 600;
}

/* 调整支付按钮样式 */
.btn-pay {
  width: 100%;
  padding: 12px;
  font-size: 14px;
  background: linear-gradient(135deg, #ff8a3d, #f7c948);
  border: none;
  color: #fff;
  cursor: pointer;
  border-radius: 14px;
  font-weight: 700;
  transition: all 0.2s ease;
  margin-top: 16px;
  box-shadow: 0 14px 28px rgba(255, 138, 61, 0.22);
}

.btn-pay:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff7a22, #f2be35);
  transform: translateY(-1px);
}

.btn-pay:disabled {
  background: #e2e8f0;
  color: #a0aec0;
  cursor: not-allowed;
}

/* Added styles for messages */
.error-message {
  color: #ff4d4f; /* Red color */
  font-size: 0.9rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.success-message {
  color: #126e6a;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.coupon-locked-message {
  display: block !important;
  color: #8b4210 !important;
  background: rgba(255, 244, 223, 0.72);
  border: 1px solid rgba(255, 138, 61, 0.22);
  border-radius: 12px;
  padding: 10px 12px !important;
}

.promotion-row {
  align-items: center;
  margin: 16px 0 10px !important;
  padding: 14px !important;
  border: 1px solid rgba(255, 138, 61, 0.36);
  border-radius: 16px;
  background:
    radial-gradient(circle at 88% 18%, rgba(247, 201, 72, 0.28), transparent 28%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.86), rgba(255, 244, 223, 0.9));
  box-shadow: 0 16px 34px rgba(255, 138, 61, 0.14);
}

.promotion-row strong {
  display: block;
  color: #8b4210;
  font-size: 16px;
}

.promotion-row small {
  display: block;
  margin-top: 4px;
  color: #5f7472;
  font-size: 12px;
  line-height: 18px;
}

.promotion-row em {
  flex: 0 0 auto;
  min-width: 112px;
  border-radius: 999px;
  padding: 7px 10px;
  color: #fff;
  background: #ff8a3d;
  font-style: normal;
  font-weight: 700;
  text-align: center;
}

.total-row {
  margin-top: 14px !important;
  padding-top: 16px !important;
  font-size: 18px;
  font-weight: 700;
}
/* Adjust summary list item style for messages */
.summary li.error-message,
.summary li.success-message {
    display: block; /* Allow message to take full width */
    text-align: left;
}

/* 修改为 Stripe 风格的返回按钮样式 */
.back-button {
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: rgba(23, 59, 58, 0.55);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1;
}

.back-button:hover {
  color: #ff7a22;
}

.back-button:active {
  transform: translateX(-2px);
}

.back-button svg {
  width: 16px;
  height: 16px;
  color: currentColor;
}

@media (max-width: 768px) {
  .checkout-content {
    flex-direction: column;
    margin-top: 40px;
    min-height: 0;
  }

  .right-panel {
    width: auto;
  }
}
</style>
